import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ListTodo, CheckCircle2, Clock, AlertTriangle, Send, Filter, LayoutGrid, Table2 } from 'lucide-react';
import { useHeaderConfig } from '@/hooks/useHeaderConfig';
import { useBlockTask, useMyTasks, useSubmitTask, useUnblockTask, useUpdateTask } from '@/api/tasks';
import { PageTransition } from '@/components/ui/PageTransition';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { Button } from '@/components/ui/Button';
import { StatsRow } from '@/components/shared/StatsRow';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { Modal } from '@/components/ui/Modal';
import { Textarea } from '@/components/ui/Textarea';
import { toast } from 'sonner';
import type { Task } from '@/types/models';

type StatusFilter = 'all' | 'active' | 'overdue' | 'blocked' | 'review' | 'done';
type ViewMode = 'table' | 'cards';

export default function MyTasks() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<StatusFilter>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [blockTaskId, setBlockTaskId] = useState('');
  const [blockReason, setBlockReason] = useState('');
  const { data: tasks = [], isLoading } = useMyTasks({ sortBy: 'deadline', sortDir: 'asc' });
  const submitTask = useSubmitTask();
  const updateTask = useUpdateTask();
  const blockTask = useBlockTask();
  const unblockTask = useUnblockTask();

  const filtered = useMemo(() => {
    if (tab === 'all') return tasks;
    if (tab === 'overdue') return tasks.filter(t => t.deadlineState === 'overdue');
    if (tab === 'active') return tasks.filter(t => t.status === 'NOT_STARTED' || t.status === 'IN_PROGRESS');
    if (tab === 'blocked') return tasks.filter(t => t.status === 'BLOCKED');
    if (tab === 'review') return tasks.filter(t => t.status === 'REVIEW' || t.approvalStatus === 'SUBMITTED');
    if (tab === 'done') return tasks.filter(t => t.status === 'DONE');
    return tasks;
  }, [tasks, tab]);

  const stats = useMemo(() => {
    const total = tasks.length;
    const active = tasks.filter(t => t.status === 'NOT_STARTED' || t.status === 'IN_PROGRESS').length;
    const overdue = tasks.filter(t => t.deadlineState === 'overdue').length;
    const blocked = tasks.filter(t => t.status === 'BLOCKED').length;
    const review = tasks.filter(t => t.status === 'REVIEW' || t.approvalStatus === 'SUBMITTED').length;
    return [
      { title: 'My Tasks', value: total, icon: <ListTodo />, iconBg: 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' },
      { title: 'Active', value: active, icon: <Clock />, iconBg: 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' },
      { title: 'Overdue', value: overdue, icon: <AlertTriangle />, iconBg: 'bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400' },
      { title: 'Blocked', value: blocked, icon: <AlertTriangle />, iconBg: 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400' },
      { title: 'Review', value: review, icon: <CheckCircle2 />, iconBg: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' },
    ];
  }, [tasks]);

  const headerConfig = useMemo(() => ({
    title: 'My Tasks',
    breadcrumbs: [{ label: 'Home', href: '/' }, { label: 'My Tasks' }],
    actions: [
      {
        type: 'toggle' as const,
        value: viewMode,
        onChange: (v: string) => setViewMode(v === 'cards' ? 'cards' : 'table'),
        options: [
          { value: 'table', label: 'Table', icon: Table2 },
          { value: 'cards', label: 'Cards', icon: LayoutGrid },
        ],
      },
      {
        type: 'filter' as const,
        label: 'Filter tasks by status',
        icon: Filter,
        options: [
          { label: 'Active', value: 'active' },
          { label: 'Overdue', value: 'overdue' },
          { label: 'Blocked', value: 'blocked' },
          { label: 'Review', value: 'review' },
          { label: 'Done', value: 'done' },
        ],
        value: tab === 'all' ? '' : tab,
        onChange: (value: string) => {
          if (value === '') setTab('all');
          else if (value === 'active' || value === 'overdue' || value === 'blocked' || value === 'review' || value === 'done') setTab(value);
        },
      },
    ],
  }), [tab, viewMode]);

  useHeaderConfig(headerConfig);

  const handleStart = async (taskId: string) => {
    try {
      await updateTask.mutateAsync({ id: taskId, status: 'IN_PROGRESS', progress: 5 });
      toast.success('Task started');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to start task');
    }
  };

  const handleSubmit = async (taskId: string) => {
    try {
      await submitTask.mutateAsync({ id: taskId });
      toast.success('Task submitted for review');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to submit task');
    }
  };

  const handleUnblock = async (taskId: string) => {
    try {
      await unblockTask.mutateAsync(taskId);
      toast.success('Blocker cleared');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to clear blocker');
    }
  };

  const handleBlock = async () => {
    if (!blockTaskId || blockReason.trim().length < 3) return;
    try {
      await blockTask.mutateAsync({ id: blockTaskId, reason: blockReason.trim() });
      toast.success('Blocker reported');
      setBlockTaskId('');
      setBlockReason('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to report blocker');
    }
  };

  const renderActions = (row: Task) => (
    <div className="flex flex-wrap justify-end gap-2">
      {row.status === 'NOT_STARTED' ? (
        <Button
          size="sm"
          variant="outline"
          className="h-9 px-3 text-xs sm:h-7 sm:px-2"
          isLoading={updateTask.isPending}
          icon={<Clock />}
          onClick={(e) => {
            e.stopPropagation();
            handleStart(row.id);
          }}
        >
          Start
        </Button>
      ) : null}
      {row.status !== 'BLOCKED' && row.status !== 'DONE' && row.status !== 'REVIEW' ? (
        <Button
          size="sm"
          variant="outline"
          className="h-9 px-3 text-xs sm:h-7 sm:px-2"
          icon={<AlertTriangle />}
          onClick={(e) => {
            e.stopPropagation();
            setBlockTaskId(row.id);
          }}
        >
          Block
        </Button>
      ) : null}
      {row.status === 'BLOCKED' ? (
        <Button
          size="sm"
          variant="outline"
          className="h-9 px-3 text-xs sm:h-7 sm:px-2"
          isLoading={unblockTask.isPending}
          icon={<CheckCircle2 />}
          onClick={(e) => {
            e.stopPropagation();
            handleUnblock(row.id);
          }}
        >
          Clear
        </Button>
      ) : null}
      {row.status === 'IN_PROGRESS' && row.approvalStatus === 'WAITING' ? (
        <Button
          size="sm"
          variant="outline"
          className="h-9 px-3 text-xs sm:h-7 sm:px-2"
          isLoading={submitTask.isPending}
          icon={<Send />}
          onClick={(e) => {
            e.stopPropagation();
            handleSubmit(row.id);
          }}
        >
          Submit
        </Button>
      ) : null}
    </div>
  );

  const columns: Column<Task>[] = [
    {
      key: 'title',
      label: 'Title',
      sticky: true,
      render: (row) => (
        <span className="font-medium text-gray-900 dark:text-gray-100">{row.title}</span>
      ),
    },
    {
      key: 'projectName',
      label: 'Project',
      render: (row) => (
        <span className="text-gray-600 dark:text-gray-400">{row.projectName || '-'}</span>
      ),
    },
    {
      key: 'priority',
      label: 'Priority',
      render: (row) => <StatusBadge status={row.priority} size="sm" />,
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => <StatusBadge status={row.status} size="sm" />,
    },
    {
      key: 'progress',
      label: 'Progress',
      render: (row) => (
        <div className="min-w-[120px]">
          <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700">
            <div
              className="h-2 rounded-full bg-primary-500"
              style={{ width: `${Math.min(Math.max(row.progress ?? 0, 0), 100)}%` }}
            />
          </div>
          <p className="mt-1 text-[11px] text-gray-500">
            {row.progress ?? 0}%{row.checklistTotal ? ` · ${row.checklistDone ?? 0}/${row.checklistTotal} checks` : ''}
          </p>
        </div>
      ),
    },
    {
      key: 'dueDate',
      label: 'Due Date',
      render: (row) => (
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {new Date(row.dueDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
        </span>
      ),
    },
    {
      key: 'actions' as keyof Task,
      label: '',
      align: 'right',
      render: renderActions,
    },
  ];

  return (
    <PageTransition>
      <div className="space-y-6">
        <StatsRow items={stats} loading={isLoading} />

        {viewMode === 'table' ? (
          <DataTable
            columns={columns}
            data={filtered}
            isLoading={isLoading}
            onRowClick={(row) => navigate(`/tasks/${row.id}`)}
            emptyTitle="No tasks found"
            emptyDescription="You have no tasks in this category"
          />
        ) : (
          <div className="rounded-xl border border-gray-200/80 bg-white shadow-soft dark:border-gray-700/60 dark:bg-gray-800/80">
            {isLoading ? (
              <div className="grid gap-4 p-4 sm:grid-cols-2 lg:grid-cols-3 lg:p-5">
                {Array.from({ length: 9 }).map((_, i) => (
                  <Skeleton key={i} className="h-44 rounded-xl" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <EmptyState
                icon={<ListTodo />}
                title="No tasks found"
                description="You have no tasks in this category"
                className="py-12"
              />
            ) : (
              <div className="grid gap-4 p-4 sm:grid-cols-2 lg:grid-cols-3 lg:p-5">
                {filtered.map((task) => (
                  <Card
                    key={task.id}
                    hover
                    padding="md"
                    onClick={() => navigate(`/tasks/${task.id}`)}
                    className="min-h-[44px]"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {task.title}
                        </p>
                        <p className="mt-1 truncate text-xs text-gray-500 dark:text-gray-400">
                          {task.projectName || 'No project'}
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1">
                        <StatusBadge status={task.status} size="sm" />
                        <StatusBadge status={task.priority} size="sm" />
                      </div>
                    </div>

                    <div className="mt-4 flex items-center justify-between gap-3 border-t border-gray-100 pt-4 dark:border-gray-700/60">
                      <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                        {task.dueDate
                          ? new Date(task.dueDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                          : 'No due date'}
                      </span>
                      {task.deadlineState === 'overdue' ? (
                        <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-600 dark:bg-rose-950/30 dark:text-rose-300">
                          Overdue
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-3">
                      <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700">
                        <div
                          className="h-2 rounded-full bg-primary-500"
                          style={{ width: `${Math.min(Math.max(task.progress ?? 0, 0), 100)}%` }}
                        />
                      </div>
                      <div className="mt-1 flex items-center justify-between gap-2 text-[11px] text-gray-500">
                        <span>{task.progress ?? 0}% complete</span>
                        {task.checklistTotal ? <span>{task.checklistDone ?? 0}/{task.checklistTotal} checks</span> : null}
                      </div>
                      {task.blockedReason ? (
                        <p className="mt-2 line-clamp-2 rounded-lg bg-rose-50 px-2 py-1 text-xs text-rose-700 dark:bg-rose-950/25 dark:text-rose-200">
                          {task.blockedReason}
                        </p>
                      ) : null}
                    </div>
                    <div className="mt-4 border-t border-gray-100 pt-3 dark:border-gray-700/60">
                      {renderActions(task)}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <Modal
        isOpen={!!blockTaskId}
        onClose={() => {
          setBlockTaskId('');
          setBlockReason('');
        }}
        title="Report blocker"
        description="Explain what is stopping progress so the manager has enough context to unblock it."
        size="sm"
        footer={(
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setBlockTaskId('');
                setBlockReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="danger"
              isLoading={blockTask.isPending}
              onClick={handleBlock}
              disabled={blockReason.trim().length < 3}
            >
              Report blocker
            </Button>
          </>
        )}
      >
        <Textarea
          label="Blocker reason"
          value={blockReason}
          onChange={(e) => setBlockReason(e.target.value)}
          rows={4}
          placeholder="Waiting on client content, access approval, API credentials..."
        />
      </Modal>
    </PageTransition>
  );
}
